function parseInitiative(value) {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function applyDraftInitiatives(entities, draftInitiatives = {}) {
  if (!draftInitiatives || Object.keys(draftInitiatives).length === 0) return entities;

  return entities.map((entity) => {
    if (!Object.prototype.hasOwnProperty.call(draftInitiatives, entity.id)) return entity;

    const draftValue = draftInitiatives[entity.id];
    if (draftValue == null) return entity;

    const trimmedValue = String(draftValue).trim();
    if (trimmedValue === '') return entity;

    const nextInitiative = parseInt(trimmedValue, 10);
    if (Number.isNaN(nextInitiative)) return entity;

    return {
      ...entity,
      initiative: nextInitiative,
    };
  });
}

function sortEncounterEntities(entities) {
  return [...entities].sort((a, b) => {
    const initiativeDiff = parseInitiative(b.initiative) - parseInitiative(a.initiative);
    if (initiativeDiff !== 0) return initiativeDiff;

    const sortOrderA = typeof a.sortOrder === 'number' ? a.sortOrder : Number.MAX_SAFE_INTEGER;
    const sortOrderB = typeof b.sortOrder === 'number' ? b.sortOrder : Number.MAX_SAFE_INTEGER;
    if (sortOrderA !== sortOrderB) return sortOrderA - sortOrderB;

    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

function getMainListEntities(entities) {
  return sortEncounterEntities(
    entities.filter((entity) => (entity.current_hp ?? entity.hp) > 0 || entity.isPlayer)
  );
}

function getDeadNonPlayers(entities) {
  return sortEncounterEntities(
    entities.filter((entity) => (entity.current_hp ?? entity.hp) <= 0 && !entity.isPlayer)
  );
}

function getNormalizedCurrentEntityId(entities, currentEntityId) {
  if (entities.some((entity) => entity.id === currentEntityId)) return currentEntityId;

  const mainListEntities = getMainListEntities(entities);
  return mainListEntities[0]?.id ?? entities[0]?.id ?? null;
}

function getNextTurnState(entities, currentEntityId, round) {
  const orderedEntities = getMainListEntities(entities);
  if (orderedEntities.length === 0) {
    return { currentEntityId: null, round: Math.max(1, round || 1) };
  }

  const currentPosition = orderedEntities.findIndex((entity) => entity.id === currentEntityId);
  if (currentPosition < 0) {
    return {
      currentEntityId: orderedEntities[0].id,
      round: Math.max(1, round || 1),
    };
  }

  const safePosition = currentPosition >= 0 ? currentPosition : 0;
  const nextPosition = (safePosition + 1) % orderedEntities.length;
  const nextRound = nextPosition === 0 ? Math.max(1, round || 1) + 1 : Math.max(1, round || 1);

  return {
    currentEntityId: orderedEntities[nextPosition].id,
    round: nextRound,
  };
}

function createEncounterEntity(baseEntity, options = {}) {
  return {
    ...baseEntity,
    id: options.id ?? baseEntity.id,
    name: options.name ?? baseEntity.name,
    groupId: options.groupId ?? baseEntity.groupId ?? null,
    groupInstance: options.groupInstance ?? baseEntity.groupInstance ?? null,
    sourceFile: options.sourceFile ?? baseEntity.sourceFile ?? null,
    sourceType: options.sourceType ?? baseEntity.sourceType ?? (baseEntity.isPlayer ? 'player' : 'monster'),
    sortOrder: options.sortOrder ?? baseEntity.sortOrder ?? 0,
    actions: Array.isArray(baseEntity.actions) ? baseEntity.actions.map((action) => ({ ...action })) : [],
    spells: Array.isArray(baseEntity.spells) ? baseEntity.spells.map((spell) => ({ ...spell })) : [],
    legendary_actions: Array.isArray(baseEntity.legendary_actions)
      ? baseEntity.legendary_actions.map((action) => ({ ...action }))
      : [],
    saving_throws: baseEntity.saving_throws ? { ...baseEntity.saving_throws } : {},
  };
}

function normalizeEncounterEntity(entity, index) {
  return createEncounterEntity(entity, {
    id: entity.id || `loaded-entity-${index + 1}`,
    sortOrder: typeof entity.sortOrder === 'number' ? entity.sortOrder : index,
    groupId: entity.groupId ?? null,
    groupInstance: entity.groupInstance ?? null,
  });
}

function serializeEncounter({ entities, round, currentEntityId }) {
  return {
    version: 1,
    round: Math.max(1, round || 1),
    currentEntityId: currentEntityId ?? null,
    entities: entities.map((entity, index) => normalizeEncounterEntity(entity, index)),
  };
}

function hydrateEncounterState(payload) {
  const entities = Array.isArray(payload?.entities)
    ? payload.entities.map((entity, index) => normalizeEncounterEntity(entity, index))
    : [];

  return {
    entities,
    round: Math.max(1, parseInt(payload?.round, 10) || 1),
    currentEntityId: getNormalizedCurrentEntityId(entities, payload?.currentEntityId ?? null),
  };
}

module.exports = {
  applyDraftInitiatives,
  createEncounterEntity,
  getDeadNonPlayers,
  getMainListEntities,
  getNextTurnState,
  getNormalizedCurrentEntityId,
  hydrateEncounterState,
  serializeEncounter,
  sortEncounterEntities,
};
